import React, { useEffect, useMemo, useState } from "react";
import trainingPeriodService from "../../services/trainingPeriodService";
import { Button } from "react-bootstrap";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";
import TrainingPeriod from "../../types/TrainingPeriod";
import { SummaryDTO, TimePercent } from "../../types/SummaryDTO";

interface TrainingPeriodListItemProps {
  trainingPeriod: TrainingPeriod;
  onPeriodDeleted: () => void;
}

// --- format helpers ---
const formatTime = (seconds?: number): string => {
  const s = typeof seconds === "number" && !isNaN(seconds) ? seconds : 0;
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const toEntries = (m: any): Array<[string, TimePercent]> => {
  if (!m) return [];

  // wariant: [["1", {...}], ["2", {...}]]
  if (Array.isArray(m)) {
    return m.filter((x) => Array.isArray(x) && x.length >= 2) as Array<[string, TimePercent]>;
  }

  // wariant: {"1": {...}, "2": {...}}
  if (typeof m === "object") {
    return Object.entries(m) as Array<[string, TimePercent]>;
  }

  return [];
};

const formatPct = (pct?: number): string => `${(pct ?? 0).toFixed(1)}%`;

// Strefy: G1..G4 rosnąco, potem numery 1..6, potem reszta alfabetycznie
const sortHrZoneKeys = (a: string, b: string): number => {
  const rank = (z: string): [number, number | string] => {
    if (z?.startsWith("G")) {
      const n = Number(z.slice(1));
      if (!Number.isNaN(n)) return [1, n]; // G1..G4
    }
    const n = Number(z);
    if (!Number.isNaN(n)) return [2, n]; // 1..6
    return [9, z]; // Z, "-", inne
  };

  const ra = rank(a);
  const rb = rank(b);
  if (ra[0] !== rb[0]) return ra[0] - rb[0];
  if (typeof ra[1] === "number" && typeof rb[1] === "number") return ra[1] - rb[1];
  return String(ra[1]).localeCompare(String(rb[1]));
};

// Domeny: kolejność biznesowa
const DOMAIN_ORDER = ["MODERATE", "HEAVY", "SEVERE", "EXTREME", "STRENGTH"];
const sortDomainKeys = (a: string, b: string): number => {
  const ia = DOMAIN_ORDER.indexOf(a);
  const ib = DOMAIN_ORDER.indexOf(b);
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
};

const TrainingPeriodListItem: React.FC<TrainingPeriodListItemProps> = ({
  trainingPeriod,
  onPeriodDeleted,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [summary, setSummary] = useState<SummaryDTO | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      if (trainingPeriod.id == null) return;

      try {
        const data = await trainingPeriodService.getSummary(trainingPeriod.id);
        setSummary(data);
        console.log("Summary:", data);
      } catch (error) {
        console.error("Error fetching summary:", error);
        setSummary(null);
      }
    };

    fetchSummary();
  }, [trainingPeriod.id]);

  const handleDeleteConfirmed = async () => {
    if (trainingPeriod.id == null) {
      console.error("Cannot delete: TrainingPeriod ID is undefined.");
      return;
    }

    try {
      await trainingPeriodService.deleteById(trainingPeriod.id);
      onPeriodDeleted();
    } catch (error) {
      console.error("Error deleting training period:", error);
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleDeleteCanceled = () => setShowDeleteModal(false);
  console.log("Summary:", JSON.stringify(summary))
  return (
    <tr>
      <td>{trainingPeriod.name}</td>
      <td>{trainingPeriod.type}</td>
      <td>{new Date(trainingPeriod.startDate).toLocaleDateString()}</td>
      <td>{new Date(trainingPeriod.endDate).toLocaleDateString()}</td>

      {/* Czas */}
      <td>{summary ? formatTime(summary.totalTimeSeconds) : "Ładowanie..."}</td>

      {/* Dystans */}
      <td>{summary ? `${(summary.totalDistance ?? 0).toFixed(2)} km` : "Ładowanie..."}</td>

      {/* Przewyższenie */}
      <td>{summary ? `${Math.round(summary.totalElevationGain ?? 0)} m` : "Ładowanie..."}</td>

{/* Strefy HR */}
<td>
  {summary ? (
    toEntries(summary.hrZones).length > 0 ? (
      <div className="d-flex flex-column">
        {toEntries(summary.hrZones).map(([zone, v]) => (
          <div key={zone} className="d-flex justify-content-between">
            <span className="me-2">{zone}</span>
            <span className="text-nowrap">
              {formatTime(Number(v?.timeSeconds ?? 0))} ({formatPct(Number(v?.percent ?? 0))})
            </span>
          </div>
        ))}
      </div>
    ) : (
      <span className="text-muted">—</span>
    )
  ) : (
    "Ładowanie..."
  )}
</td>

{/* Domeny */}
<td>
  {summary ? (
    toEntries(summary.domains).length > 0 ? (
      <div className="d-flex flex-column">
        {toEntries(summary.domains).map(([domain, v]) => (
          <div key={domain} className="d-flex justify-content-between">
            <span className="me-2">{domain}</span>
            <span className="text-nowrap">
              {formatTime(Number(v?.timeSeconds ?? 0))} ({formatPct(Number(v?.percent ?? 0))})
            </span>
          </div>
        ))}
      </div>
    ) : (
      <span className="text-muted">—</span>
    )
  ) : (
    "Ładowanie..."
  )}
</td>

      {/* Akcje */}
      <td>
        <Button
          variant="primary"
          onClick={() => alert("Edytuj funkcjonalność jeszcze nie gotowa")}
          className="me-2"
        >
          Edytuj
        </Button>
        <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
          Usuń
        </Button>

        <DeleteConfirmationModal
          show={showDeleteModal}
          onClose={handleDeleteCanceled}
          onDelete={handleDeleteConfirmed}
        />
      </td>
    </tr>
  );
};

export default TrainingPeriodListItem;